import React from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import { useQuery } from '@tanstack/react-query';
import Portlet from '@app/component/portlet/Portlet';
import _isEmpty from 'lodash/isEmpty';
import Utilities from '@util/Utilities';
import MarketInsightFlightStat from '@app/model/market_insight/MarketInsightFlightStat';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';

// regionName: set when the region has 2 or more markets. The card reads the region, so it shows the region name.
const MarketInsightFlightStatsFragment: React.FunctionComponent<{
	statDate: string,
	compareForDaysBefore?: number | undefined,
	regionName?: string | null
}> = ({ statDate, compareForDaysBefore, regionName }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: flightStats, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findFlightStats({
			companyHotelId: companyHotel.id,
			statDate: statDate,
			beforeDays: compareForDaysBefore,
		}),
		queryKey: MarketInsightQueryKeys.flightStats(companyHotel.id, statDate),
	});

	if (isLoading) {
		return <Portlet title="Top Origin Markets">
			<div className="animate-pulse flex items-center gap-x-2">
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
			</div>
		</Portlet>;
	}
	if (isSuccess && flightStats !== undefined && flightStats !== null && !_isEmpty(flightStats)) {

		return <Portlet title="Top Origin Markets" note={[regionName !== undefined && regionName !== null ? `${regionName} region` : '', compareForDaysBefore !== undefined && compareForDaysBefore > 0 ? `(*past ${compareForDaysBefore} days)` : ''].filter(part => part !== '').join(' · ')}>
			<dl className="-my-3 divide-y divide-gray-100 py-4 text-sm/6">
				{flightStats.map(stat => <RenderFlightStats stat={stat} key={stat.origin} />)}
			</dl>
		</Portlet>;

	}

	return <Portlet title="Top Origin Markets">
		<p className="block text-xs font-light text-gray-400">Not Available</p>
	</Portlet>;
};

const RenderFlightStats: React.FunctionComponent<{ stat: MarketInsightFlightStat }> = ({ stat }) => {
	return <div className="flex justify-between gap-x-2 py-1.5">
		<dt className="text-gray-500 text-sm/6 font-medium">{Utilities.snakeCaseToWords(stat.origin)}</dt>
		<dd className="flex items-center gap-x-6">
			<div className="flex items-center gap-x-2">
				{stat.currentValue > stat.previousValue && <div
					className="inline-flex items-baseline rounded-full bg-green-100 px-1 py-1 text-sm font-medium text-green-800">
					<ArrowUpIcon className="w-4 h-4 shrink-0 self-center text-green-500" />
				</div>}
				{stat.currentValue < stat.previousValue && <div
					className="inline-flex items-baseline rounded-full bg-red-100 px-1 py-1 text-sm font-medium text-red-800">
					<ArrowDownIcon className="w-4 h-4 shrink-0 self-center text-red-500" />
				</div>}
				<div className="overflow-hidden rounded-full w-28 bg-gray-100">
					<div className={`h-2 rounded-full ${findBackgroundColor(stat.currentValue)} w-[78%]`}
							 style={{ width: `${(stat.currentValue / 8) * 100}%` }}></div>
				</div>
			</div>
		</dd>
	</div>;
};



const findBackgroundColor = (rating: number) => {
	if(rating < 2) return "bg-green-500";
	if(rating < 4 && rating >= 2) return "bg-amber-500";
	if(rating < 6 && rating >= 4) return "bg-pink-500";
	if(rating >= 6) return "bg-red-500";
	return "bg-green-500";
}


export default MarketInsightFlightStatsFragment;